import { AuthProvider, ThemeContextProvider } from '@gravitate-js/excalibrr'
import '@gravitate-js/excalibrr/dist/index.css'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { connectWebSocket, disconnectWebSocket } from '@hooks/websocket/websocketService'
import { BrowserRouter as Router } from 'react-router-dom'
import { Bootstrapper } from './Bootstrapper'
import { useSetup } from './api/useSetup'
import CacheBuster from './components/_SiteWide/CacheBuster'
import { themeConfigs } from './components/_SiteWide/Theming/themeconfigs'
import { useIntegrations } from './integrations'
import { useCallback, useEffect, useMemo } from 'react'
import { getAPIURL } from './utils/api'
import { getAntdLocale } from './utils/intl/language'
import { AxiosResponse } from 'axios'
import { useUserSettings } from './hooks/settings'
import api from '@utils/api'

const queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false, retry: false } } })

// Moving this initialization to the top level so we have access to the query client
function Integrations() {
  useIntegrations()
  return null
}
const apiURL = getAPIURL()

export const App = () => {
  useEffect(() => {
    connectWebSocket()
    return () => disconnectWebSocket()
  }, [])

  try {
    return (
      <Sentry.ErrorBoundary showDialog>
        <QueryClientProvider client={queryClient}>
          <Integrations />
          <GateKeeper />
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    )
  } catch (e) {
    console.error(e)
  }
}

function GateKeeper() {
  const { data } = useSetup()
  const { setupSettings } = useUserSettings()

  const onSSOLogin = useCallback(async () => {
    try {
      const resp = (await api.getAuthorization()) as AxiosResponse<Gravitate.AuthorizationResponse>
      const auth = resp.data
      setupSettings(auth.settings || {})
    } catch (error) {
      console.error('Error fetching authorization:', error)
    }
  }, [setupSettings])

  const locale = getAntdLocale()
  return (
    <AuthProvider
      baseUrl={apiURL}
      ssoConfig={{
        providers: data?.ssoProviders || [],
        authenticateUrl: `${apiURL}sso/token`,
        callbackUrl: `${window.location.origin}/auth/callback`,
        onSuccess: onSSOLogin,
      }}
    >
      <Router>
        <ThemeContextProvider themeConfigs={themeConfigs} locale={locale}>
          <CacheBuster>
            {({ loading, isLatestVersion, refreshCacheAndReload }) => {
              if (loading) return null
              if (!loading && !isLatestVersion) {
                refreshCacheAndReload()
              }
              return <Bootstrapper />
            }}
          </CacheBuster>
        </ThemeContextProvider>
      </Router>
    </AuthProvider>
  )
}
